import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useGetServerlessInstancePricing } from '@/service/serverless-gpu/hook';
import { Loader2 } from 'lucide-react';

const GPUInstancePricing = () => {
  const { data: instancePricing, isLoading } = useGetServerlessInstancePricing();

  return (
    <div className="container py-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold">Pricing</h1>
        <p className="text-muted-foreground mt-1">
          GPU Instance pricing by type and region
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Instance Types</CardTitle>
          <CardDescription>
            Available GPU instance configurations and their pricing
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="size-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Instance Type</TableHead>
                  <TableHead>Provider</TableHead>
                  <TableHead>Region</TableHead>
                  <TableHead className="text-right">On-Demand ($/hr)</TableHead>
                  <TableHead className="text-right">Spot ($/hr)</TableHead>
                  <TableHead className="text-right">Storage ($/GB/mo)</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {instancePricing?.map((instance) => (
                  <TableRow key={instance.instance_id}>
                    <TableCell className="font-medium">
                      {instance.instance_type}
                    </TableCell>
                    <TableCell>{instance.instance_provider}</TableCell>
                    <TableCell>{instance.instance_region}</TableCell>
                    <TableCell className="text-right">
                      ${instance.price_per_hour.toFixed(2)}
                    </TableCell>
                    <TableCell className="text-right">
                      ${instance.spot_price_per_hour.toFixed(2)}
                    </TableCell>
                    <TableCell className="text-right">
                      ${instance.storage_price_per_month.toFixed(2)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <div className="grid gap-6 md:grid-cols-2 mt-8">
        <Card>
          <CardHeader>
            <CardTitle>Billing Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-muted-foreground">
            <p>
              <strong className="text-foreground">On-Demand:</strong> Pay by the hour with no
              long-term commitments. Perfect for development and testing.
            </p>
            <p>
              <strong className="text-foreground">Spot:</strong> Access spare GPU capacity at
              significantly reduced rates. Great for fault-tolerant workloads.
            </p>
            <p>
              <strong className="text-foreground">Storage:</strong> Persistent storage is billed
              monthly based on provisioned capacity.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Usage Notes</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-muted-foreground">
            <p>
              Billing starts when the instance is created and stops when it is
              terminated.
            </p>
            <p>
              Minimum billing increment is 1 minute for compute instances.
            </p>
            <p>
              Data transfer within the same region is free. Egress charges may
              apply for data transferred outside.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default GPUInstancePricing;
